import React, { useState, useEffect, useRef } from "react";
import { BASE_URL } from "./configUrls";
import api from "./api";

const MOUDetailsModal = ({ isOpen, onClose }) => {
  const [mouData, setMouData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Email selection states
  const [availableSigners, setAvailableSigners] = useState([]);
  const [loadingSigners, setLoadingSigners] = useState(false);
  const [selectedSigner, setSelectedSigner] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // MOU PDF iframe state
  const [showMouPdf, setShowMouPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Calculate dropdown position
  useEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [dropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Cleanup PDF URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    const fetchMOUDetails = async () => {
      if (!isOpen) return;

      try {
        setLoading(true);
        setError(null);
        const accessToken = localStorage.getItem("accessToken");

        const response = await fetch(`${BASE_URL}/accounts/spoc/mou-status`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch MOU details");
        }

        const data = await response.json();
        setMouData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMOUDetails();
  }, [isOpen]);

  // Fetch available signers when MOU data is loaded
  useEffect(() => {
    if (
      mouData &&
      mouData.mou_id &&
      mouData.status !== "signed" &&
      mouData.status !== "verified"
    ) {
      fetchAvailableSigners();
    }
  }, [mouData]);

  const fetchAvailableSigners = async () => {
    try {
      setLoadingSigners(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      const response = await api.get(
        `/accounts/available-signers/${mouData.mou_id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const { available_people } = response.data;
      setAvailableSigners(available_people || []);
    } catch (error) {
      console.error("Error fetching available signers:", error);
      setAvailableSigners([]);
    } finally {
      setLoadingSigners(false);
    }
  };

  const handleDelegate = async () => {
    if (
      !selectedSigner ||
      mouData.status === "signed" ||
      mouData.status === "verified"
    )
      return;

    try {
      setSubmitting(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      // Create FormData object
      const formData = new FormData();
      formData.append("mou_id", mouData.mou_id);
      formData.append("selected_person_id", selectedSigner.id);

      const response = await api.post("/accounts/delegate-signing", formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Delegation successful:", response.data);

      // Refresh MOU data after successful delegation
      const mouResponse = await fetch(`${BASE_URL}/accounts/spoc/mou-status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (mouResponse.ok) {
        const updatedData = await mouResponse.json();
        setMouData(updatedData);
      }

      // Reset selection
      setSelectedSigner(null);
    } catch (error) {
      console.error("Error delegating signing authority:", error);

      // Extract and display the detail message from API response
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else if (error.message) {
        alert(`Error: ${error.message}`);
      } else {
        alert("An error occurred while delegating signing authority");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to convert base64 to blob
  const base64ToBlob = (base64, contentType = "") => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  const handleViewMou = async () => {
    try {
      setLoadingPdf(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        alert("No access token found. Please login again.");
        return;
      }

      // Call download API endpoint
      const response = await api.get(
        `/accounts/spoc/download-mou/${mouData.mou_id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Extract base64 string from response
      const { pdf_base64 } = response.data;

      // Convert base64 to blob
      const blob = base64ToBlob(pdf_base64, "application/pdf");

      // Create blob URL
      const url = URL.createObjectURL(blob);

      // Revoke old URL if exists
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }

      setPdfUrl(url);
      setShowMouPdf(true);
    } catch (error) {
      console.error("Error loading MOU:", error);
      alert("Failed to load MOU. Please try again.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleClosePdf = () => {
    setShowMouPdf(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  if (!isOpen) return null;

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      signed: "bg-blue-100 text-blue-800",
      verified: "bg-purple-100 text-purple-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium inline-block capitalize ${
          statusStyles[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  const isSigningDisabled =
    mouData?.status === "signed" || mouData?.status === "verified";
  const isVerified = mouData?.status === "verified";

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">MOU Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="p-6 overflow-auto max-h-[calc(90vh-140px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">Error: {error}</p>
              </div>
            ) : mouData ? (
              <div>
                {/* Warning Message - If Someone Declined */}
                {mouData.declined_by_name && (
                  <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700 font-medium">
                          {mouData.declined_by_name} declined to sign. Please
                          choose another person or sign yourself.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* MOU Details Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                          MOU Number
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                          Signing Authority
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border min-w-[350px]">
                          Email
                          {!isSigningDisabled && (
                            <span className="text-blue-600 normal-case font-normal text-xs ml-1">
                              (Select to delegate)
                            </span>
                          )}
                        </th>
                        {mouData.is_delegated && mouData.delegated_to_email && (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 uppercase border bg-blue-50">
                            Transferred To Email
                          </th>
                        )}
                        {mouData.declined_by_name && (
                          <>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 uppercase border bg-red-50">
                              Declined By
                            </th>
                            {mouData.declined_by_email && (
                              <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 uppercase border bg-red-50">
                                Declined By Email
                              </th>
                            )}
                          </>
                        )}
                        {isVerified && (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 border">
                          {mouData.mou_number}
                        </td>
                        <td className="px-4 py-3 text-sm border">
                          {getStatusBadge(mouData.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 border">
                          {mouData.signing_authority_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 border">
                          {isSigningDisabled ? (
                            // Read-only display when signed or verified
                            <div className="text-gray-900">
                              {mouData.signing_authority_email}
                            </div>
                          ) : (
                            // Custom dropdown when not signed/verified
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <button
                                  ref={buttonRef}
                                  type="button"
                                  onClick={() => setDropdownOpen(!dropdownOpen)}
                                  disabled={loadingSigners || submitting}
                                  className="w-full px-3 py-2 text-left text-sm border border-gray-300 rounded-md bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                  {loadingSigners ? (
                                    <span className="flex items-center gap-2 text-gray-500">
                                      <svg
                                        className="animate-spin h-4 w-4"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                          fill="none"
                                        />
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                      </svg>
                                      Loading...
                                    </span>
                                  ) : selectedSigner ? (
                                    <span className="block truncate">
                                      {selectedSigner.email}
                                    </span>
                                  ) : (
                                    <span className="text-gray-500">
                                      {mouData.signing_authority_email ||
                                        "Select email..."}
                                    </span>
                                  )}
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg
                                      className="h-5 w-5 text-gray-400"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </span>
                                </button>
                              </div>

                              {/* Delegate Button */}
                              {selectedSigner && (
                                <button
                                  onClick={handleDelegate}
                                  disabled={submitting}
                                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                  {submitting ? (
                                    <span className="flex items-center gap-2">
                                      <svg
                                        className="animate-spin h-4 w-4"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                          fill="none"
                                        />
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                      </svg>
                                      sending...
                                    </span>
                                  ) : (
                                    "Send"
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        {mouData.is_delegated && mouData.delegated_to_email && (
                          <td className="px-4 py-3 text-sm text-gray-900 border bg-blue-50">
                            {mouData.delegated_to_email}
                          </td>
                        )}
                        {mouData.declined_by_name && (
                          <>
                            <td className="px-4 py-3 text-sm text-gray-900 border bg-red-50">
                              {mouData.declined_by_name}
                            </td>
                            {mouData.declined_by_email && (
                              <td className="px-4 py-3 text-sm text-gray-900 border bg-red-50">
                                {mouData.declined_by_email}
                              </td>
                            )}
                          </>
                        )}
                        {isVerified && (
                          <td className="px-4 py-3 text-sm border">
                            <button
                              onClick={handleViewMou}
                              disabled={loadingPdf}
                              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {loadingPdf ? (
                                <span className="flex items-center gap-2">
                                  <svg
                                    className="animate-spin h-4 w-4"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      fill="none"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                  Loading...
                                </span>
                              ) : (
                                "View MOU"
                              )}
                            </button>
                          </td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* MOU PDF Viewer - Iframe */}
                {isVerified && showMouPdf && pdfUrl && (
                  <div className="mt-6 border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        MOU Document
                      </h3>
                      <button
                        onClick={handleClosePdf}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="w-full h-[600px] border border-gray-300 rounded-lg overflow-hidden bg-gray-100">
                      <iframe
                        src={pdfUrl}
                        className="w-full h-full"
                        title="MOU Document"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No MOU data available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Dropdown Menu - Fixed Positioning (Outside table) */}
      {dropdownOpen && !loadingSigners && (
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 10000,
          }}
          className="mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {availableSigners.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No signers available
            </div>
          ) : (
            availableSigners.map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => {
                  setSelectedSigner(person);
                  setDropdownOpen(false);
                }}
                className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-100"
              >
                <div className="text-sm font-semibold text-gray-900">
                  {person.email}
                </div>
                <div className="text-xs text-gray-600">
                  {person.name}
                  {person.designation && ` · ${person.designation}`}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </>
  );
};

export default MOUDetailsModal;
